import { prop } from 'ramda'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { generateApiErrorPayload } from 'api-client/helpers'
import { MessageTypes } from 'core/components/notifications/model'
import ModalForm from 'core/elements/modal/ModalForm'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { NotificationType, notificationActions } from 'core/notifications/notificationReducers'
import type { Route } from 'core/plugins/route'
import { useToast } from 'core/providers/ToastProvider'
import { chameleonActions } from 'core/session/chameleonReducers'
import { type SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { isOpenCloud } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import { isSelfServiceRole } from 'k8s/util/helpers'
import { listHostNetworkConfigs } from 'openstack/components/infrastructure/actions'
import { listClusterHostGroups } from 'openstack/components/infrastructure/clusters/actions'
import { clusterHostGroupsSelector } from 'openstack/components/infrastructure/clusters/selectors'
import { withRetry } from 'utils/async'
import { trackEvent } from 'utils/tracking'
import { listHostAggregates } from '../infrastructure/actions'
import { listVolumeSnapshots } from '../storage/snapshots/actions'
import {
  volumeSnapshotsByIdSelector,
  volumeSnapshotsSelector,
} from '../storage/snapshots/selectors'
import {
  listAvailableVolumes,
  listNonPaginatedVolumes,
  pollAvailableVolumes,
  pollNonPaginatedVolumes,
} from '../storage/volumes/actions'
import {
  availableVolumesSelector,
  nonPaginatedVolumesByIdSelector,
  nonPaginatedVolumesSelector,
} from '../storage/volumes/selectors'
import { createVirtualMachine, createVirtualMachines } from './actions'
import {
  CREATE_VM_AVAILABILITY_ZONE_ERROR,
  CREATE_VM_MODAL_TITLE,
  type CreateVmFormParams,
  createDefaultCreateVmParams,
  getCreateVmStepNumber,
  getInsufficientIpManagedNetwork,
  getMinAvailableManagedNetwork,
  isWindowsVmSource,
  shouldForceSingleVmCount,
  useResetNetworksOnClusterChange,
} from './CreateVirtualMachineModal.helpers'
import useCreateVirtualMachineModalStyles from './CreateVirtualMachineModal.styles'
import CreateVirtualMachineAttachVolumesStep from './createVirtualMachineSteps/AttachVolumesStep'
import CreateVirtualMachineCustomizeStep, {
  useCreateVirtualMachineCustomizeStepController,
} from './createVirtualMachineSteps/CustomizeStep'
import CreateVirtualMachineFlavorStep, {
  useCreateVirtualMachineFlavorStepController,
} from './createVirtualMachineSteps/FlavorStep'
import CreateVirtualMachineNetworkingStep, {
  useCreateVirtualMachineNetworkingStepController,
} from './createVirtualMachineSteps/NetworkingStep'
import CreateVirtualMachineSourceStep, {
  useCreateVirtualMachineSourceStepController,
} from './createVirtualMachineSteps/SourceStep'
import { type NetworkPortsResult, useNetworkPorts } from './hooks/useNetworkPorts'
import { buildVMRequestBody } from './utils/vmBodyBuilder'
import { IVirtualMachinesListPageTabs } from './VirtualMachinesListPage'
import VmCredentialsPanel from './VmCredentialsPanel'

export {
  getPrivateIpValidator,
  hotplugMemoryMiBValidator,
  hotplugVcpuMaxValidator,
  hotplugVcpuValidator,
  placeholderCloudInitYaml,
} from './CreateVirtualMachineModal.helpers'
export { ErrorText } from './CreateVirtualMachineModal.shared'

const modalTitle = CREATE_VM_MODAL_TITLE
const returnStepNumber = getCreateVmStepNumber

interface Props {
  addRoute: Route
  reloadVirtualMachines: () => void
}

export default function CreateVirtualMachineModal({ addRoute, reloadVirtualMachines }: Props) {
  const { history, match } = useReactRouter()
  const classes = useCreateVirtualMachineModalStyles()
  const dispatch = useDispatch()
  const showToast = useToast()

  // Helpers that create network ports in Neutron up front and delete them again if VM
  // creation fails. Works for both simple (Layer 2) and managed networks.
  const { preCreatePorts, cleanupPreCreatedPorts } = useNetworkPorts()

  const [showVmPassword, setShowVmPassword] = useState(false)
  const [vmPassword, setVmPassword] = useState('')
  const [minAvailableIpsNetwork, setMinAvailableIpsNetwork] = useState(null)
  const [isoCreateError, setIsoCreateError] = useState(null)
  const [portCreationError, setPortCreationError] = useState<{
    title: string
    message: string
  } | null>(null)
  const [submitPending, setSubmitPending] = useState(false)
  const submitPendingRef = useRef(false)
  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { features } = session
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  // Hide the "boot from volume" option only when the feature flag is explicitly false.
  // If it's undefined or anything else, the option stays enabled.
  const disabledBootFromVolume = features?.experimental?.boot_from_volume === false

  const defaultParams = createDefaultCreateVmParams(disabledBootFromVolume)
  const { prefs } = useScopedPreferences()
  const currentTenant = prefs.currentTenant as string
  const { params, getParamsUpdater, setParams, updateParams } =
    useParams<CreateVmFormParams>(defaultParams)
  const isSelfServiceUser = isSelfServiceRole(session)
  const isOpen = addRoute.pattern.match(match.url)

  const { loading: loadingVolumes } = useListAction(listNonPaginatedVolumes, {
    params: {},
  })
  const volumes = useSelector(nonPaginatedVolumesSelector)

  // List of available volumes, used both for the "Existing Volume" source and the
  // "Attach Volumes" step.
  const { loading: loadingAvailableVolumes, reload: reloadAvailableVolumes } = useListAction(
    listAvailableVolumes,
    {
      loadOnDemand: true,
    },
  )
  const { updating: updatingAvailableVolumes, update: updateAvailableVolumes } =
    useUpdateAction(pollAvailableVolumes)
  const { update: updateNonPaginatedVolumes } = useUpdateAction(pollNonPaginatedVolumes)
  const availableVolumesRaw = useSelector(availableVolumesSelector)

  const volumesById = useSelector(nonPaginatedVolumesByIdSelector)

  const { loading: loadingVolumeSnapshots } = useListAction(listVolumeSnapshots, {
    params: {},
  })
  const volumeSnapshots = useSelector(volumeSnapshotsSelector)
  const volumeSnapshotsById = useSelector(volumeSnapshotsByIdSelector)

  const { loading: loadingClusterHostGroups } = useListAction(listClusterHostGroups)
  useListAction(listHostNetworkConfigs)
  const clusterHostGroups = useSelector(clusterHostGroupsSelector)

  const sourceStep = useCreateVirtualMachineSourceStepController({
    availableVolumesRaw,
    currentTenant,
    isOpen,
    loadingAvailableVolumes,
    loadingVolumeSnapshots,
    loadingVolumes,
    params,
    reloadAvailableVolumes,
    updatingAvailableVolumes,
    volumeSnapshots,
    volumeSnapshotsById,
    volumesById,
  })
  const flavorStep = useCreateVirtualMachineFlavorStepController({ params })
  const selectedCluster = useMemo(
    () => clusterHostGroups?.find((c) => c.name === params.availabilityZone),
    [clusterHostGroups, params.availabilityZone],
  )
  const networkingStep = useCreateVirtualMachineNetworkingStepController({
    clusterId: params.availabilityZone,
    applicableNetworkLabels: selectedCluster?.physicalNetworkLabels ?? [],
    currentTenant,
    isSelfServiceUser,
  })
  const customizeStep = useCreateVirtualMachineCustomizeStepController()

  useListAction(listHostAggregates)

  const { update, updating, error, reset } = useUpdateAction(createVirtualMachine)
  const {
    update: createMultiple,
    updating: creatingMultiple,
    error: errorMultiple,
    reset: resetMultiple,
  } = useUpdateAction(createVirtualMachines)
  const responseError = error || errorMultiple

  useEffect(() => {
    if (!isOpen) {
      return
    }
    // Once every list has finished loading, send an analytics event with how many of each
    // resource (images, volumes, flavors, networks, clusters) the user actually has.
    if (
      !sourceStep.loadingImages &&
      !loadingVolumes &&
      !flavorStep.loadingFlavors &&
      !networkingStep.loadingNetworks &&
      !loadingClusterHostGroups
    ) {
      const body = {
        images: sourceStep.images?.length,
        volumes: volumes?.length,
        flavors: flavorStep.flavors?.length,
        networks: networkingStep.networksList?.length,
        networksWithFreeIps: networkingStep.networksList?.filter(
          (network) => network?.availableIps > 0,
        )?.length,
        clusters: clusterHostGroups?.length,
      }
      trackEvent(`${modalTitle} - Resources Present`, body)
    }
  }, [
    sourceStep.loadingImages,
    loadingVolumes,
    flavorStep.loadingFlavors,
    networkingStep.loadingNetworks,
    loadingClusterHostGroups,
    match.url,
  ])

  // Each time the modal opens, force-refresh the lists so the user sees current data.
  useEffect(() => {
    if (!isOpen) {
      return
    }
    // Refresh available volumes
    updateAvailableVolumes()
    // Refresh the full (non-paginated) volumes list
    updateNonPaginatedVolumes()
    // Refresh tenant config and the source-step resources
    sourceStep.reloadSourceStepResources()
    // Refresh networks
    networkingStep.reloadNetworks(true)
  }, [match.url]) // Key off the URL, not isOpen — isOpen is a new object on every render

  useEffect(() => {
    if (shouldForceSingleVmCount(params)) {
      updateParams({
        count: 1,
      })
    }
  }, [params.sourceType, params.attachVolumes])

  // FR-017, FR-019: Clear the chosen networks and subnets whenever the cluster changes,
  // since they may not be valid on the new cluster.
  useResetNetworksOnClusterChange({
    availabilityZone: params.availabilityZone,
    networks: params.networks,
    selectedSubnets: params.selectedSubnets,
    updateParams,
  })

  const releaseSubmitLock = useCallback(() => {
    submitPendingRef.current = false
    setSubmitPending(false)
  }, [])

  const getVolumeSnapshotSourceVolume = (volumeSnapshot) => {
    return volumesById[volumeSnapshot?.volume_id]
  }

  /**
   * Creates several VMs at once, all in parallel, retrying each one on its own if it fails.
   * Works for both simple and managed network ports.
   * FR-013: when a hostname is given, each VM gets it with a "-<number>" suffix (e.g. web-1, web-2).
   * Reports how many succeeded vs failed, cleans up ports for the failures, and notifies the user.
   */
  const createBatchVMsInParallel = async (portsByVm: NetworkPortsResult[]): Promise<void> => {
    const results = await Promise.allSettled(
      Array.from({ length: params.count }, async (_, i) => {
        const vmPorts = portsByVm[i] || {}
        const vmName = params.count > 1 ? `${params.name}-${i + 1}` : params.name
        const vmHostname =
          params.hostname && params.count > 1 ? `${params.hostname}-${i + 1}` : params.hostname
        const body = buildVMRequestBody(
          { ...params, count: 1, name: vmName, hostname: vmHostname } as any,
          { managedNetworkPorts: vmPorts },
        )
        return withRetry(
          async () => {
            // Call the action directly (instead of the hook) so the VMs really run in
            // parallel — the hook would run them one after another. No error thrown = created.
            const response = (await createVirtualMachine.call(
              { body },
              { propagateError: true },
            )) as any
            // A successful call returns the new VM (with id and adminPass). Make sure we got
            // an id back; if not, treat it as a failure.
            const vmId = response?.id
            if (!vmId) {
              console.error('VM creation response missing id:', response)
              throw new Error('VM creation response missing ID')
            }
            return { vmName, id: vmId }
          },
          2,
          5000,
        )
      }),
    )

    const successes: string[] = []
    const failures: { vmName: string; error: string }[] = []

    await Promise.all(
      results.map(async (result, i) => {
        const vmPorts = portsByVm[i] || {}
        const vmName = params.count > 1 ? `${params.name}-${i + 1}` : params.name
        if (result.status === 'fulfilled') {
          successes.push(result.value.id)
          console.debug(`[Batch] VM ${vmName} created successfully (attempt succeeded)`)
        } else {
          failures.push({
            vmName,
            error: result.reason?.message || 'Unknown error',
          })
          console.warn(
            `[Batch] VM ${vmName} creation failed after retries:`,
            result.reason?.message,
          )
          await cleanupPreCreatedPorts(vmPorts)
        }
      }),
    )

    if (successes.length > 0) {
      trackEvent(`${modalTitle} - Finished`, {
        vmIds: successes,
        batchSize: params.count,
        failureCount: failures.length,
      })
      reloadVirtualMachines()
      handleClose()

      if (failures.length === 0) {
        showToast(`${successes.length} VM(s) created successfully.`, MessageTypes.success)
      } else {
        showToast(
          `${successes.length}/${params.count} VMs created. ${failures.length} failed.`,
          MessageTypes.warning,
        )
        dispatch(
          notificationActions.registerNotification({
            title: `Batch VM creation: ${failures.length} failure(s)`,
            message: failures.map((f) => `${f.vmName}: ${f.error}`).join('\n'),
            type: NotificationType.error,
            data: { successCount: successes.length, failureCount: failures.length },
          }),
        )
      }
    } else {
      dispatch(
        notificationActions.registerNotification({
          title: `Batch VM creation failed (${params.count} VMs)`,
          message: failures.map((f) => `${f.vmName}: ${f.error}`).join('\n'),
          type: NotificationType.error,
          data: { totalCount: params.count, failureCount: failures.length },
        }),
      )
    }
  }

  const submitForm = useCallback(async () => {
    if (submitPendingRef.current) {
      return
    }
    submitPendingRef.current = true
    setSubmitPending(true)
    setIsoCreateError(null)
    try {
      // Find the managed network with the fewest free IPs — it's the limiting factor for
      // how many VMs we can make. (E.g. with 5 networks that each have 1 free IP, only 1
      // VM is possible.) Simple networks are skipped here because they pre-create ports.
      const networkWithMinIps = getMinAvailableManagedNetwork(params?.networks)
      setMinAvailableIpsNetwork(networkWithMinIps ?? null)

      // FR-019: a managed network needs (VM count × its auto-allocating rows) free IPs,
      // since a multi-subnet network gives each VM one IP per row. Bail out with an error
      // if any network can't cover that.
      const insufficientIpNetwork = getInsufficientIpManagedNetwork(
        params?.networks,
        params.networkAttachments ?? {},
        params.count,
      )
      if (insufficientIpNetwork) {
        const { network, required } = insufficientIpNetwork
        dispatch(
          notificationActions.registerNotification({
            title: 'Insufficient IP addresses',
            message: `${network.name} has only ${network?.availablePortAndIPs} available IP(s), but ${required} needed for ${params.count} VM(s).`,
            type: NotificationType.error,
            data: {
              requestedCount: params.count,
              requiredIPs: required,
              availableIPs: network?.availablePortAndIPs,
            },
          }),
        )
        return
      }

      // Only managed-network rows with a chosen subnet need a port made in Neutron before the VM
      // is created (FR-016). Simple (L2) networks are not pre-created — Nova creates their ports.
      const emptyPorts: NetworkPortsResult = {}
      let preCreatedPorts: NetworkPortsResult | NetworkPortsResult[] = emptyPorts
      const hasManagedSubnetSelections = Object.values(params.networkAttachments ?? {}).some(
        (rows) => (rows as any[]).some((row) => row.subnet !== '' && row.port === ''),
      )

      if (hasManagedSubnetSelections) {
        try {
          preCreatedPorts = await preCreatePorts(
            params.networks,
            params.networkAttachments ?? {},
            currentTenant,
            params.name,
            params.count,
            // PCD-5420: put the user's chosen security groups directly on the pre-created
            // ports. Nova ignores the server-level security_groups for ports that already
            // exist, so this is the only way they take effect. Empty list when SGs are off.
            params.useSecurityGroups ? params.securityGroups.map((sg: any) => sg.id) : [],
          )
        } catch (error) {
          console.error('Port pre-creation failed:', error)
          // Pull a readable error out of the raw API/Neutron error and show it to the user
          const errorPayload = generateApiErrorPayload(error, '', 'Failed to create network ports')
          const neutronError =
            (error as any)?.response?.data?.NeutronError || (error as any)?.NeutronError
          const errorTitle = neutronError?.type || 'Port pre-creation failed'

          setPortCreationError({
            title: errorTitle,
            message: errorPayload?.message || 'Failed to create network ports',
          })
          return
        }
      }

      // Use the per-VM parallel path ONLY when each VM needs something Nova's batch API can't
      // give every instance from one request: a pre-created managed port (each VM needs its own
      // distinct port) or a per-VM hostname suffix (FR-013). Otherwise use the single batch API
      // call below — this is what physical/managed-automatic networks use, and L2 networks now
      // use it too (they send { uuid } and Nova creates one port per VM in the batch).
      const needsBatchCreation =
        params.count > 1 && (hasManagedSubnetSelections || !!params.hostname)

      if (needsBatchCreation) {
        const portsByVm: NetworkPortsResult[] = Array.isArray(preCreatedPorts)
          ? preCreatedPorts
          : Array.from({ length: params.count }, () => ({}))
        return await createBatchVMsInParallel(portsByVm)
      }

      const ports = preCreatedPorts as NetworkPortsResult
      const cleanupAllPorts = () => cleanupPreCreatedPorts(ports)
      const hasPreCreatedPorts = Object.keys(ports).length > 0

      try {
        // Assemble the request payload Nova expects from the form values and ports
        const body = buildVMRequestBody(params as any, {
          managedNetworkPorts: ports,
        })
        const updateFn = params.count === 1 ? update : createMultiple
        const { success, response } = await updateFn({ body })
        if (success) {
          const eventBody =
            params.count > 1
              ? {
                  vmIds: response?.map((res) => res?.id || 'N/A'),
                }
              : {
                  vmId: response?.id,
                }
          // Remember the new VM's id so we can check later whether it became active
          if (params.count === 1) {
            dispatch(
              chameleonActions.updateChameleonState({
                createdVmId: response?.id,
              }),
            )
          }
          trackEvent(`${modalTitle} - Finished`, eventBody)
          reloadVirtualMachines()
          if (params.count > 1) {
            handleClose()
            return
          }
          // For non-Windows VMs there's no admin password to show, so just close the wizard
          const isWindows = isWindowsVmSource(params, getVolumeSnapshotSourceVolume)
          if (!isWindows) {
            handleClose()
            return
          }
          // PCD-8538: hide the windows vm password modal for now
          // PCD-3767: show the correct windows VM password to the user so they can log in to the new Windows VM
          /*
          setVmPassword(response?.adminPass)
          setShowVmPassword(true)
          */
          handleClose()
          return
        } else {
          trackEvent(`${modalTitle} - Create API Failure`)
          // VM creation failed, so delete the ports we made up front to avoid orphans
          if (hasPreCreatedPorts) await cleanupAllPorts()
        }
      } catch (vmCreationError) {
        console.error('VM creation failed:', vmCreationError)
        // VM creation threw, so delete the ports we made up front to avoid orphans
        if (hasPreCreatedPorts) await cleanupAllPorts()
        if (params.sourceType === 'installFromIso') {
          const message =
            generateApiErrorPayload(vmCreationError, '', 'Failed to create VM from ISO')?.message ||
            vmCreationError?.message ||
            'Failed to create VM from ISO'
          setIsoCreateError({ title: '', message })
          return
        }
        // Re-throw so the error surfaces to the user
        throw vmCreationError
      }
    } finally {
      releaseSubmitLock()
    }
  }, [params, currentTenant, volumesById, releaseSubmitLock])

  const handleClose = () => {
    history.push(
      routeManager.routes.openstack.vms.path({
        vmstab: IVirtualMachinesListPageTabs.VirtualMachinesList,
      }),
    )
    setShowVmPassword(false)
    setVmPassword('')
    setIsoCreateError(null)
    setPortCreationError(null)
    releaseSubmitLock()
    reset()
    resetMultiple()
    sourceStep.resetSourceStepState()
    flavorStep.resetFlavorStepState()
    networkingStep.resetNetworkingStepState()
    setParams(defaultParams)
  }

  const isClusterRequired = !(isVmware && isOpenCloud())

  useEffect(() => {
    if (showVmPassword) {
      const modalBody = document.querySelector('.modal-body')
      modalBody.scrollTo(0, 0)
    }
  }, [showVmPassword])

  return (
    <ModalForm
      route={addRoute}
      title={modalTitle}
      onSubmit={showVmPassword ? handleClose : submitForm}
      onClose={handleClose}
      submitting={updating || creatingMultiple || submitPending}
      disableSubmit={(step) =>
        params.sourceType === 'installFromIso' && step === 1 && !params.installerIso?.[0]
      }
      disableSubmitMessage={(step) =>
        params.sourceType === 'installFromIso' && step === 1 && !params.installerIso?.[0]
          ? 'Please select an installer ISO to continue.'
          : ''
      }
      steps={returnStepNumber(params.sourceType, 5)}
      error={
        portCreationError ||
        isoCreateError ||
        (responseError?.message === CREATE_VM_AVAILABILITY_ZONE_ERROR
          ? {
              message: `The selected cluster '${params.availabilityZone}' does not have any hosts assigned. Please assign a host to it or select a different cluster`,
            }
          : responseError)
      }
      submitTitle={showVmPassword ? `Finish` : `Deploy Virtual Machine`}
      hideBack={showVmPassword}
      maxWidth={900}
      showConfirmationOnCancel
    >
      {({ step }) => (
        <>
          {!showVmPassword ? (
            <>
              {step === 1 && (
                <CreateVirtualMachineSourceStep
                  {...sourceStep}
                  getParamsUpdater={getParamsUpdater}
                  isClusterRequired={isClusterRequired}
                  params={params}
                  updateParams={updateParams}
                />
              )}
              {step === 2 && !['image', 'snapshot'].includes(params.sourceType) && (
                <CreateVirtualMachineAttachVolumesStep
                  availableVolumesRaw={availableVolumesRaw}
                  getParamsUpdater={getParamsUpdater}
                  loadingAvailableVolumes={loadingAvailableVolumes}
                  params={params}
                  reloadAvailableVolumes={reloadAvailableVolumes}
                  updatingAvailableVolumes={updatingAvailableVolumes}
                />
              )}
              {step === returnStepNumber(params.sourceType, 3) && (
                <CreateVirtualMachineFlavorStep
                  {...flavorStep}
                  params={params}
                  updateParams={updateParams}
                />
              )}
              {step === returnStepNumber(params.sourceType, 4) && (
                <CreateVirtualMachineNetworkingStep
                  {...networkingStep}
                  getParamsUpdater={getParamsUpdater}
                  params={params}
                  setMinAvailableIpsNetwork={setMinAvailableIpsNetwork}
                  updateParams={updateParams}
                />
              )}
              {step === returnStepNumber(params.sourceType, 5) && (
                <CreateVirtualMachineCustomizeStep
                  {...customizeStep}
                  getParamsUpdater={getParamsUpdater}
                  minAvailableIpsNetwork={minAvailableIpsNetwork}
                  params={params}
                  updateParams={updateParams}
                  vmNetworkMode={networkingStep.vmNetworkMode}
                />
              )}
            </>
          ) : (
            <div className={classes.passwordScreen}>
              <VmCredentialsPanel vmPassword={vmPassword} />
            </div>
          )}
        </>
      )}
    </ModalForm>
  )
}
